import React, { useState, useEffect, useRef } from 'react';
import { X, CreditCard, Check, Sparkles, Calendar, Clock, RefreshCw, Zap, Upload, FileImage, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentPlanModal = ({ isOpen, onClose, userType, onPaymentSuccess }) => {
  const [plans, setPlans] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState({});
  const [selectedPlan, setSelectedPlan] = useState('annual'); // Default to annual (mejor valor)
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [paymentMode, setPaymentMode] = useState('recurring'); // 'recurring' o 'single'
  const [loading, setLoading] = useState(false);
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [currency, setCurrency] = useState('MXN');
  const [location, setLocation] = useState(null);
  
  // Bank transfer proof upload
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const fileInputRef = useRef(null);
  
  // Proration for monthly billing aligned to 1st
  const [proration, setProration] = useState(null);
  const [loadingProration, setLoadingProration] = useState(false);
  
  // Trial info
  const [trialInfo, setTrialInfo] = useState(null);
  const [loadingTrial, setLoadingTrial] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
      fetchTrialInfo();
    }
  }, [isOpen]);
  
  // Fetch proration when monthly plan is selected
  useEffect(() => {
    if (isOpen && selectedPlan === 'monthly' && paymentMode === 'recurring') {
      fetchProration();
    }
  }, [isOpen, selectedPlan, paymentMode]);
  
  const fetchTrialInfo = async () => {
    try {
      setLoadingTrial(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/subscriptions/trial-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialInfo(response.data);
    } catch (error) {
      console.error('Error fetching trial info:', error);
    } finally {
      setLoadingTrial(false);
    }
  };
  
  const fetchProration = async () => {
    try {
      setLoadingProration(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/subscriptions/calculate-proration?plan_type=monthly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.proration_applies) {
        setProration(response.data);
      }
    } catch (error) {
      console.error('Error fetching proration:', error);
    } finally {
      setLoadingProration(false);
    }
  };

  const fetchPlans = async () => {
    try {
      setFetchingPlans(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/payments/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data.plans);
      setPaymentMethods(response.data.payment_methods);
      setCurrency(response.data.currency || 'MXN');
      setLocation(response.data.location || null);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Error al cargar los planes');
    } finally {
      setFetchingPlans(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes (JPG, PNG, WEBP) o PDF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no debe superar 5MB');
      return;
    }

    setProofFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setProofPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setProofPreview(null);
    }
  };

  const handleUploadProof = async () => {
    if (!proofFile) {
      toast.error('Selecciona un comprobante de pago');
      return;
    }

    setUploadingProof(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('proof_file', proofFile);
      formData.append('plan_type', selectedPlan);
      formData.append('amount', plans?.[selectedPlan]?.price || 0);

      const response = await axios.post(
        `${API}/api/payments/bank-transfer-proof`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('¡Comprobante enviado! Te notificaremos cuando se verifique tu pago.');
      setProofFile(null);
      setProofPreview(null);
      onClose();
    } catch (error) {
      console.error('Error uploading proof:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar comprobante');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedPlan || !selectedMethod) {
      toast.error('Selecciona un plan y método de pago');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    const plan = plans[selectedPlan];

    try {
      let response;
      
      // Suscripción recurrente (solo con Stripe)
      if (paymentMode === 'recurring' && selectedMethod === 'stripe') {
        response = await axios.post(
          `${API}/api/subscriptions/create-checkout`,
          {
            plan_type: selectedPlan, // 'monthly' o 'annual'
            origin_url: window.location.origin
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.url) {
          toast.success('Redirigiendo a Stripe para activar tu suscripción recurrente...');
          window.location.href = response.data.url;
        }
        return;
      }
      
      // Pago único con Mercado Pago
      if (selectedMethod === 'mercadopago') {
        response = await axios.post(
          `${API}/api/payments/mercadopago/checkout`,
          {
            package_type: plan.id,
            origin_url: window.location.origin
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.url) {
          window.location.href = response.data.url;
        }
        return;
      }
      
      // Pago con OXXO
      if (selectedMethod === 'oxxo') {
        response = await axios.post(
          `${API}/api/checkout/oxxo`,
          {
            package_type: plan.id,
            origin_url: window.location.origin
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          // Guardar el client_secret para confirmar el pago
          localStorage.setItem('oxxo_client_secret', response.data.client_secret);
          localStorage.setItem('oxxo_payment_intent', response.data.payment_intent_id);
          
          // Redirigir a página de instrucciones OXXO
          window.location.href = `/payment-oxxo?amount=${response.data.amount}&description=${encodeURIComponent(response.data.description)}`;
        }
        return;
      }
      
      // Pago único con Stripe (tarjeta)
      response = await axios.post(
        `${API}/api/payments/checkout`,
        {
          package_type: plan.id,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a2e] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-purple-500/30">
        {/* Header */}
        <div className="sticky top-0 bg-[#1a1a2e] border-b border-purple-500/20 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Elige tu Plan</h2>
            <p className="text-white/60 text-sm mt-1">Selecciona el plan que mejor se adapte a ti</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="close-payment-modal"
          >
            <X className="w-6 h-6 text-white/70" />
          </button>
        </div>

        {fetchingPlans ? (
          <div className="p-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <div className="p-6 space-y-8">
            {/* Location/Currency Indicator */}
            {location && (
              <div className="flex items-center justify-center gap-2 text-sm text-white/60 bg-black/20 rounded-lg py-2 px-4">
                <span className="text-lg">{location.is_mexico ? '🇲🇽' : '🌎'}</span>
                <span>
                  {location.is_mexico 
                    ? `Precios en Pesos Mexicanos (${currency})` 
                    : `Precios en Dólares Americanos (${currency}) - Internacional`
                  }
                </span>
              </div>
            )}

            {/* Trial Banner - if available */}
            {trialInfo?.trial_available && (
              <div className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/40 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/30 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">🎉 ¡{trialInfo.trial_days} días de prueba GRATIS!</h4>
                    <p className="text-white/70 text-sm">Prueba sin compromiso hasta el {trialInfo.trial_end_display}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Al terminar el trial:</p>
                    <p className="text-white font-semibold">${trialInfo.first_charge?.amount} MXN</p>
                    <p className="text-white/50 text-xs">{trialInfo.first_charge?.description}</p>
                  </div>
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Desde el {trialInfo.first_full_charge?.display}:</p>
                    <p className="text-white font-semibold">${trialInfo.first_full_charge?.amount} MXN/mes</p>
                    <p className="text-white/50 text-xs">Renovación automática el día 1</p>
                  </div>
                </div>
                {trialInfo.discount_applied > 0 && (
                  <p className="text-purple-400 text-xs mt-2 text-center">
                    ✨ Tu descuento de referido ({trialInfo.discount_applied}%) se aplica después del trial
                  </p>
                )}
              </div>
            )}

            {/* Billing Info Banner */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-center">
              <p className="text-white/80 text-sm">
                📅 <strong>Facturación unificada:</strong> Todas las membresías se renuevan el <strong>día 1 de cada mes</strong>.
                {!trialInfo?.trial_available && ' Hoy pagas solo el prorrateo por los días restantes.'}
              </p>
            </div>
            
            {/* Plans Selection */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Monthly Plan */}
              {plans?.monthly && (
                <div
                  onClick={() => setSelectedPlan('monthly')}
                  className={`relative cursor-pointer rounded-xl p-6 border-2 transition-all ${
                    selectedPlan === 'monthly'
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-white/10 hover:border-white/30 bg-black/20'
                  }`}
                  data-testid="plan-monthly"
                >
                  {/* Badge de descuento por referido */}
                  {plans.monthly.referral_discount && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {plans.monthly.discount_percent}% descuento referido
                      </span>
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-4 mt-2">
                    <div>
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Clock className="w-5 h-5 text-purple-400" />
                        {plans.monthly.name}
                      </h3>
                      <p className="text-white/60 text-sm mt-1">{plans.monthly.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'monthly' ? 'border-purple-500 bg-purple-500' : 'border-white/30'
                    }`}>
                      {selectedPlan === 'monthly' && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-white">${plans.monthly.price.toLocaleString()}</span>
                      <span className="text-white/60">{currency}/{plans.monthly.billing_period}</span>
                    </div>
                    {plans.monthly.referral_discount && plans.monthly.original_price > plans.monthly.price && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-white/40 line-through text-sm">
                          ${plans.monthly.original_price.toLocaleString()} {currency}
                        </span>
                        <span className="bg-purple-500/20 text-purple-400 text-xs px-2 py-0.5 rounded">
                          -{plans.monthly.discount_percent}%
                        </span>
                      </div>
                    )}
                    {plans.monthly.referral_months_remaining > 0 && (
                      <p className="text-purple-400 text-xs mt-1">
                        Te quedan {plans.monthly.referral_months_remaining} mes(es) con descuento
                      </p>
                    )}
                  </div>

                  <ul className="space-y-2">
                    {plans.monthly.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Annual Plan */}
              {plans?.annual && (
                <div
                  onClick={() => setSelectedPlan('annual')}
                  className={`relative cursor-pointer rounded-xl p-6 border-2 transition-all ${
                    selectedPlan === 'annual'
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-white/10 hover:border-white/30 bg-black/20'
                  }`}
                  data-testid="plan-annual"
                >
                  {/* Badge */}
                  {plans.annual.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {plans.annual.badge}
                      </span>
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-4 mt-2">
                    <div>
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-green-400" />
                        {plans.annual.name}
                      </h3>
                      <p className="text-white/60 text-sm mt-1">{plans.annual.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'annual' ? 'border-green-500 bg-green-500' : 'border-white/30'
                    }`}>
                      {selectedPlan === 'annual' && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-white">${plans.annual.price.toLocaleString()}</span>
                      <span className="text-white/60">{currency}/{plans.annual.billing_period}</span>
                    </div>
                    {plans.annual.original_price && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-white/40 line-through text-sm">
                          ${plans.annual.original_price.toLocaleString()} {currency}
                        </span>
                        <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded">
                          -{plans.annual.discount_percent}%
                        </span>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-2">
                    {plans.annual.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Enrollment Plan for Authors */}
            {plans?.enrollment && (
              <div className="border-t border-purple-500/20 pt-6">
                <h3 className="text-lg font-bold text-white mb-4">Inscripción de Autor (Opcional)</h3>
                <div
                  onClick={() => setSelectedPlan('enrollment')}
                  className={`cursor-pointer rounded-xl p-6 border-2 transition-all ${
                    selectedPlan === 'enrollment'
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-white/10 hover:border-white/30 bg-black/20'
                  }`}
                  data-testid="plan-enrollment"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-bold text-white">{plans.enrollment.name}</h4>
                      <p className="text-white/60 text-sm mt-1">{plans.enrollment.description}</p>
                      {plans.enrollment.note && (
                        <p className="text-amber-400 text-xs mt-2">* {plans.enrollment.note}</p>
                      )}
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'enrollment' ? 'border-amber-500 bg-amber-500' : 'border-white/30'
                    }`}>
                      {selectedPlan === 'enrollment' && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">${plans.enrollment.price.toLocaleString()}</span>
                    <span className="text-white/60 ml-2">{currency} (pago único)</span>
                  </div>

                  <ul className="space-y-2">
                    {plans.enrollment.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                        <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* NEW: Author Membership Plan ($4,997) - Sin prorrateo */}
            {plans?.author_membership && (
              <div className="border-t border-amber-500/20 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-bold rounded-full">MEMBRESÍA</span>
                  <h3 className="text-lg font-bold text-white">Paso 1: Membresía de Autor</h3>
                </div>
                <div
                  onClick={() => setSelectedPlan('author_membership')}
                  className={`cursor-pointer rounded-xl p-6 border-2 transition-all ${
                    selectedPlan === 'author_membership'
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-white/10 hover:border-white/30 bg-black/20'
                  }`}
                  data-testid="plan-author-membership"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-bold text-white">{plans.author_membership.name}</h4>
                      <p className="text-white/60 text-sm mt-1">{plans.author_membership.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'author_membership' ? 'border-amber-500 bg-amber-500' : 'border-white/30'
                    }`}>
                      {selectedPlan === 'author_membership' && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">${plans.author_membership.price.toLocaleString()}</span>
                    <span className="text-white/60 ml-2">{currency}/año</span>
                  </div>

                  <ul className="space-y-2 mb-4">
                    {plans.author_membership.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                        <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs">
                    <p className="text-amber-400">
                      💡 <strong>Cobro inmediato sin prorrateo.</strong> {plans.author_membership.note}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* NEW: Author Subscription Plan ($199/mes) - Con prorrateo */}
            {plans?.author_subscription && (
              <div className="border-t border-purple-500/20 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full">SUSCRIPCIÓN</span>
                  <h3 className="text-lg font-bold text-white">Paso 2: Suscripción Mensual</h3>
                </div>
                <div
                  onClick={() => setSelectedPlan('author_subscription')}
                  className={`cursor-pointer rounded-xl p-6 border-2 transition-all ${
                    selectedPlan === 'author_subscription'
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-white/10 hover:border-white/30 bg-black/20'
                  }`}
                  data-testid="plan-author-subscription"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-bold text-white">{plans.author_subscription.name}</h4>
                      <p className="text-white/60 text-sm mt-1">{plans.author_subscription.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'author_subscription' ? 'border-purple-500 bg-purple-500' : 'border-white/30'
                    }`}>
                      {selectedPlan === 'author_subscription' && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    {plans.author_subscription.discount_percent > 0 ? (
                      <>
                        <span className="text-xl text-white/50 line-through mr-2">${plans.author_subscription.original_price}</span>
                        <span className="text-3xl font-bold text-white">${plans.author_subscription.price.toLocaleString()}</span>
                        <span className="ml-2 text-green-400 text-sm">-{plans.author_subscription.discount_percent}%</span>
                      </>
                    ) : (
                      <span className="text-3xl font-bold text-white">${plans.author_subscription.price.toLocaleString()}</span>
                    )}
                    <span className="text-white/60 ml-2">{currency}/mes</span>
                  </div>

                  <ul className="space-y-2 mb-4">
                    {plans.author_subscription.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                        <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-xs">
                    <p className="text-purple-400">
                      📅 <strong>Se prorratea al día 1 del mes.</strong> {plans.author_subscription.note}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Author Status Summary */}
            {plans?.author_status && (
              <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-xl p-4 border border-white/10">
                <h4 className="text-sm font-bold text-white/80 mb-2">Tu estado como Autor:</h4>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className={`p-2 rounded ${plans.author_status.has_membership ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {plans.author_status.has_membership ? '✓' : '○'} Membresía {plans.author_status.has_membership ? 'Activa' : 'Pendiente'}
                  </div>
                  <div className={`p-2 rounded ${plans.author_status.has_subscription ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {plans.author_status.has_subscription ? '✓' : '○'} Suscripción {plans.author_status.has_subscription ? 'Activa' : 'Pendiente'}
                  </div>
                </div>
                {!plans.author_status.pool_monetization && plans.author_status.has_membership && (
                  <p className="text-amber-400 text-xs mt-2">
                    ⚠️ Activa tu suscripción para monetizar el Pool de Autores al 100%
                  </p>
                )}
              </div>
            )}

            {/* Payment Mode Selection - Recurrente vs Único */}
            {(selectedPlan === 'monthly' || selectedPlan === 'annual') && (
              <div className="border-t border-purple-500/20 pt-6">
                <h3 className="text-lg font-bold text-white mb-2">Tipo de Pago</h3>
                <p className="text-white/60 text-sm mb-4">Elige cómo deseas pagar tu suscripción</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Suscripción Recurrente */}
                  <div
                    onClick={() => {
                      setPaymentMode('recurring');
                      setSelectedMethod('stripe'); // Recurrente solo con Stripe
                    }}
                    className={`cursor-pointer rounded-xl p-4 border-2 transition-all ${
                      paymentMode === 'recurring'
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-white/10 hover:border-white/30 bg-black/20'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                        paymentMode === 'recurring' ? 'border-purple-500 bg-purple-500' : 'border-white/30'
                      }`}>
                        {paymentMode === 'recurring' && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <RefreshCw className="w-5 h-5 text-purple-400" />
                          <p className="font-semibold text-white">Suscripción Automática</p>
                        </div>
                        <p className="text-white/60 text-xs mt-1">
                          Se cobra automáticamente cada {selectedPlan === 'monthly' ? 'mes' : 'año'}. 
                          Puedes cancelar cuando quieras.
                        </p>
                        <div className="mt-2 flex items-center gap-1">
                          <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">Recomendado</span>
                          <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded">Sin preocupaciones</span>
                        </div>
                        
                        {/* Proration info for monthly plans */}
                        {selectedPlan === 'monthly' && proration && (
                          <div className="mt-3 p-3 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg border border-purple-500/20">
                            <p className="text-white/90 text-sm font-medium mb-2">📅 Facturación unificada al día 1</p>
                            <div className="space-y-1 text-xs">
                              <p className="text-white/70">
                                <span className="text-green-400 font-semibold">Hoy pagas:</span>{' '}
                                <span className="text-white">${proration.today_charge?.toFixed(2)} MXN</span>
                                <span className="text-white/50 ml-1">({proration.today_charge_description})</span>
                              </p>
                              <p className="text-white/70">
                                <span className="text-purple-400 font-semibold">Renovación:</span>{' '}
                                <span className="text-white">${proration.monthly_charge?.toFixed(2)} MXN/mes</span>
                                <span className="text-white/50 ml-1">(desde el {proration.first_renewal_display})</span>
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pago Único */}
                  <div
                    onClick={() => setPaymentMode('single')}
                    className={`cursor-pointer rounded-xl p-4 border-2 transition-all ${
                      paymentMode === 'single'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-white/10 hover:border-white/30 bg-black/20'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                        paymentMode === 'single' ? 'border-blue-500 bg-blue-500' : 'border-white/30'
                      }`}>
                        {paymentMode === 'single' && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Zap className="w-5 h-5 text-blue-400" />
                          <p className="font-semibold text-white">Pago Único</p>
                        </div>
                        <p className="text-white/60 text-xs mt-1">
                          Paga manualmente cada {selectedPlan === 'monthly' ? 'mes' : 'año'} cuando venza tu suscripción.
                        </p>
                        <div className="mt-2">
                          <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">Control total</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Method Selection */}
            <div className="border-t border-purple-500/20 pt-6">
              <h3 className="text-lg font-bold text-white mb-4">
                {paymentMode === 'recurring' ? 'Método de Pago (Suscripción Automática)' : 'Método de Pago'}
              </h3>
              
              {/* Si es recurrente, solo mostrar Stripe */}
              {paymentMode === 'recurring' ? (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-8 h-8 text-purple-400" />
                    <div>
                      <p className="font-semibold text-white">Stripe (Tarjeta de crédito/débito)</p>
                      <p className="text-white/60 text-xs">Las suscripciones automáticas se procesan con Stripe</p>
                    </div>
                    <div className="ml-auto">
                      <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                {/* Stripe */}
                {paymentMethods.stripe && (
                  <div
                    onClick={() => setSelectedMethod('stripe')}
                    className={`cursor-pointer rounded-xl p-4 border-2 transition-all flex items-center gap-4 ${
                      selectedMethod === 'stripe'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-white/10 hover:border-white/30 bg-black/20'
                    }`}
                    data-testid="method-stripe"
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedMethod === 'stripe' ? 'border-blue-500 bg-blue-500' : 'border-white/30'
                    }`}>
                      {selectedMethod === 'stripe' && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-8 h-8 text-blue-400" />
                      <div>
                        <p className="font-semibold text-white">Stripe</p>
                        <p className="text-white/60 text-xs">Tarjeta de crédito/débito</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mercado Pago */}
                {paymentMethods.mercadopago && (
                  <div
                    onClick={() => setSelectedMethod('mercadopago')}
                    className={`cursor-pointer rounded-xl p-4 border-2 transition-all flex items-center gap-4 ${
                      selectedMethod === 'mercadopago'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-white/10 hover:border-white/30 bg-black/20'
                    }`}
                    data-testid="method-mercadopago"
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedMethod === 'mercadopago' ? 'border-cyan-500 bg-cyan-500' : 'border-white/30'
                    }`}>
                      {selectedMethod === 'mercadopago' && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">MP</span>
                      </div>
                      <div>
                        <p className="font-semibold text-white">Mercado Pago</p>
                        <p className="text-white/60 text-xs">Múltiples opciones de pago</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* OXXO - Solo pago único */}
                {paymentMethods.stripe && (
                  <div
                    onClick={() => setSelectedMethod('oxxo')}
                    className={`cursor-pointer rounded-xl p-4 border-2 transition-all flex items-center gap-4 ${
                      selectedMethod === 'oxxo'
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-white/10 hover:border-white/30 bg-black/20'
                    }`}
                    data-testid="method-oxxo"
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedMethod === 'oxxo' ? 'border-orange-500 bg-orange-500' : 'border-white/30'
                    }`}>
                      {selectedMethod === 'oxxo' && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-xs">OXXO</span>
                      </div>
                      <div>
                        <p className="font-semibold text-white">OXXO</p>
                        <p className="text-white/60 text-xs">Pago en efectivo en tienda</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transferencia Bancaria (CLABE) */}
                <div
                  onClick={() => setSelectedMethod('bank_transfer')}
                  className={`cursor-pointer rounded-xl p-4 border-2 transition-all flex items-center gap-4 col-span-2 ${
                    selectedMethod === 'bank_transfer'
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-white/10 hover:border-white/30 bg-black/20'
                  }`}
                  data-testid="method-bank-transfer"
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedMethod === 'bank_transfer' ? 'border-emerald-500 bg-emerald-500' : 'border-white/30'
                  }`}>
                    {selectedMethod === 'bank_transfer' && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-xs">CLABE</span>
                    </div>
                    <div>
                      <p className="font-semibold text-white">Transferencia Bancaria</p>
                      <p className="text-white/60 text-xs">SPEI / Transferencia con CLABE</p>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Bank Transfer Info - Solo para pago único */}
              {paymentMode === 'single' && selectedMethod === 'bank_transfer' && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-4">
                  <h4 className="font-semibold text-emerald-400 mb-3">Datos para Transferencia Bancaria</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/60">Banco:</span>
                      <span className="text-white font-mono">BBVA México</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">CLABE:</span>
                      <span className="text-white font-mono">012180026180320848</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Concepto:</span>
                      <span className="text-white font-mono">VIUBUX-{Date.now().toString().slice(-8)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Monto:</span>
                      <span className="text-emerald-400 font-bold">${plans?.[selectedPlan]?.price?.toLocaleString() || '0'} {currency}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Summary and Checkout Button */}
            <div className="border-t border-purple-500/20 pt-6">
              <div className="bg-black/30 rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Plan seleccionado:</span>
                  <span className="text-white font-semibold">
                    {plans?.[selectedPlan]?.name || 'Ninguno'}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-white/70">Total a pagar:</span>
                  <span className="text-2xl font-bold text-green-400">
                    ${plans?.[selectedPlan]?.price?.toLocaleString() || '0'} {currency}
                  </span>
                </div>
                {selectedPlan === 'annual' && plans?.annual?.savings > 0 && (
                  <div className="text-center mt-2">
                    <span className="text-green-400 text-sm">
                      ¡Ahorras ${plans.annual.savings.toLocaleString()} {currency}!
                    </span>
                  </div>
                )}
              </div>

              {selectedMethod !== 'bank_transfer' ? (
                <>
                  <button
                    onClick={handleCheckout}
                    disabled={loading || !selectedPlan || !selectedMethod}
                    className="w-full py-4 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    data-testid="checkout-button"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Procesando...
                      </span>
                    ) : paymentMode === 'recurring' ? (
                      <span className="flex items-center justify-center gap-2">
                        <RefreshCw className="w-5 h-5" />
                        Activar Suscripción Automática
                      </span>
                    ) : (
                      `Pagar con ${selectedMethod === 'stripe' ? 'Stripe' : 'Mercado Pago'}`
                    )}
                  </button>

                  <p className="text-center text-white/40 text-xs mt-4">
                    {paymentMode === 'recurring' 
                      ? 'Se cobrará automáticamente cada período. Puedes cancelar cuando quieras.'
                      : `Pago seguro procesado por ${selectedMethod === 'stripe' ? 'Stripe' : 'Mercado Pago'}`
                    }
                  </p>
                </>
              ) : (
                <div className="space-y-4">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="hidden"
                  />

                  {/* Upload area */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      proofFile
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-white/20 hover:border-emerald-500/50 hover:bg-white/5'
                    }`}
                  >
                    {proofPreview ? (
                      <div className="space-y-3">
                        <img
                          src={proofPreview}
                          alt="Comprobante"
                          className="max-h-32 mx-auto rounded-lg"
                        />
                        <p className="text-emerald-400 text-sm">{proofFile?.name}</p>
                        <p className="text-white/50 text-xs">Clic para cambiar</p>
                      </div>
                    ) : proofFile ? (
                      <div className="space-y-3">
                        <FileImage className="w-12 h-12 mx-auto text-emerald-400" />
                        <p className="text-emerald-400 text-sm">{proofFile.name}</p>
                        <p className="text-white/50 text-xs">Clic para cambiar</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Upload className="w-12 h-12 mx-auto text-white/40" />
                        <p className="text-white/70">
                          Sube tu comprobante de pago
                        </p>
                        <p className="text-white/40 text-xs">
                          JPG, PNG, WEBP o PDF (máx. 5MB)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('012180026180320848');
                        toast.success('CLABE copiada al portapapeles');
                      }}
                      className="flex-1 py-3 rounded-xl font-medium text-white transition-all bg-white/10 hover:bg-white/20 border border-white/20"
                    >
                      Copiar CLABE
                    </button>
                    <button
                      onClick={handleUploadProof}
                      disabled={!proofFile || uploadingProof}
                      className="flex-1 py-3 rounded-xl font-bold text-white transition-all bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="upload-proof-button"
                    >
                      {uploadingProof ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          Enviar Comprobante
                        </>
                      )}
                    </button>
                  </div>

                  <p className="text-center text-white/40 text-xs">
                    Tu suscripción se activará una vez verificado el pago (máx. 24 hrs)
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPlanModal;
